import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, UploadCloud, Loader2, Link2, Save } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { gbp } from "@/lib/pricing";
import { Input } from "@/components/ui/input";

const STATUS = { awaiting_payment: "Awaiting payment", confirming: "Confirming", paid: "Paid", failed: "Failed", cancelled: "Cancelled" };
const FULFIL = { pending: "Not yet shipped", processing: "Being prepared", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled" };
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

const Stat = ({ label, value, testId }) => (
  <div className="border border-border bg-card p-4" data-testid={testId}>
    <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
    <div className="mt-1 font-heading text-2xl font-black tracking-tight">{value}</div>
  </div>
);

export const AffiliatePanel = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ brand_name: "", tagline: "", accent_color: "", ticker_text: "", payout_addresses: { BTC: "", LTC: "" }, support_telegram: "", support_email: "", support_url: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/me/affiliate").then((r) => {
    setData(r.data);
    const s = r.data.settings || {};
    setForm({ brand_name: s.brand_name || "", tagline: s.tagline || "", accent_color: s.accent_color || "", ticker_text: s.ticker_text || "",
      payout_addresses: { BTC: s.payout_addresses?.BTC || "", LTC: s.payout_addresses?.LTC || "" },
      support_telegram: s.support_telegram || "", support_email: s.support_email || "", support_url: s.support_url || "" });
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data || !data.settings?.enabled) return null;
  const { settings, stats } = data;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/affiliate", { ...form, accent_color: form.accent_color || "" });
      toast.success("Your branding is saved");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/me/affiliate/logo", fd);
      toast.success("Logo uploaded");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const white = settings.tier !== "referral";
  const shopUrl = white ? (settings.shop_url || settings.link) : settings.link;
  const copyLink = () => { navigator.clipboard?.writeText(shopUrl); toast.success("Link copied"); };

  return (
    <section className="mt-10" data-testid="affiliate-panel" data-tier={white ? "whitelabel" : "referral"}>
      <div className="mono text-[11px] uppercase tracking-[0.25em] text-primary">{white ? "Your affiliate shop" : "Your referral link"}</div>
      <h2 className="mt-1 font-heading text-lg font-black tracking-tight">{white ? "Send customers to your own branded shop" : "Share your link, earn on every order"}</h2>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
        {white
          ? <>Anyone who opens your link sees <b>{settings.brand_name}</b> instead of our brand, pays normal retail prices, and you earn the difference between retail and your trade price on every paid order.</>
          : <>Anyone who opens your link shops the normal DreamLabs site at retail prices; every paid order they place is credited to you and you earn the difference between retail and your trade price.</>}
      </p>

      <div className="mt-4 flex flex-col gap-2 border border-primary/40 bg-primary/5 p-4 sm:flex-row sm:items-center" data-testid="affiliate-link-box">
        <Link2 className="h-4 w-4 shrink-0 text-primary" />
        <code className="mono flex-1 break-all text-sm" data-testid="affiliate-link">{shopUrl}</code>
        <button data-testid="affiliate-copy-link" onClick={copyLink} className="inline-flex items-center justify-center gap-1.5 bg-primary px-4 py-2 text-xs font-medium text-primary-foreground">
          <Copy className="h-3.5 w-3.5" /> Copy link
        </button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Referred orders" value={stats.orders} testId="affiliate-stat-orders" />
        <Stat label="Paid sales" value={gbp(stats.paid_sales)} testId="affiliate-stat-sales" />
        <Stat label="Commission earned" value={gbp(stats.commission_earned)} testId="affiliate-stat-commission" />
        <Stat label="Awaiting payment" value={gbp(stats.commission_pending)} testId="affiliate-stat-pending" />
        <Stat label="Paid out to you" value={gbp(stats.paid_out || 0)} testId="affiliate-stat-paid-out" />
        <Stat label="Still owed to you" value={gbp(stats.owed || 0)} testId="affiliate-stat-owed" />
      </div>
      {(stats.payouts || []).length > 0 && (
        <div className="mt-3 border border-border bg-card p-4" data-testid="affiliate-payouts">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Payout history</div>
          <ul className="mt-2 divide-y divide-border text-xs">
            {stats.payouts.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-1.5" data-testid={`affiliate-payout-${p.id}`}>
                <span className="mono text-muted-foreground">{fmtWhen(p.created_at)}</span>
                <span className="flex-1 truncate">{p.note || "Commission payout"}</span>
                <span className="mono font-semibold">{gbp(p.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.2fr]">
        <div className="border border-border bg-card p-5" data-testid="affiliate-brand-editor">
          {white && (<>
          <div className="font-heading text-base font-bold">Your branding</div>
          <div className="mt-4 flex items-center gap-4">
            <div className="flex h-20 w-32 items-center justify-center border border-border bg-background">
              {settings.logo_url
                ? <img src={settings.logo_url} alt={settings.brand_name} className="max-h-16 max-w-[7rem] object-contain" data-testid="affiliate-logo-preview" />
                : <span className="mono text-[10px] uppercase text-muted-foreground">No logo</span>}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="affiliate-logo-input" onChange={(e) => upload(e.target.files?.[0])} />
              <button data-testid="affiliate-logo-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="inline-flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-medium hover:border-primary">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />} Upload logo
              </button>
              <p className="mt-1 text-[10px] text-muted-foreground">PNG / JPG / WebP, transparent background looks best.</p>
            </div>
          </div>
          <label className="mono mt-4 block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Shop name</label>
          <Input data-testid="affiliate-brand-name" value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} className="mt-1 rounded-none" placeholder="e.g. Lulu's Syrups" />
          <label className="mono mt-3 block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Tagline (footer)</label>
          <Input data-testid="affiliate-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="mt-1 rounded-none" placeholder="Premium flavour syrups, delivered discreetly." />
          <label className="mono mt-3 block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Scrolling top bar (optional)</label>
          <Input data-testid="affiliate-ticker" value={form.ticker_text} maxLength={200} onChange={(e) => setForm({ ...form, ticker_text: e.target.value })} className="mt-1 rounded-none" placeholder="Free UK shipping over £160 · Crypto only · Discreet packaging" />
          <p className="mt-1 text-[11px] text-muted-foreground">Shown across the top of your shop. Leave blank for no bar.</p>
          <label className="mono mt-3 block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Accent colour (optional hex)</label>
          <div className="mt-1 flex items-center gap-2">
            <input type="color" value={form.accent_color || "#7c3aed"} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-9 w-12 cursor-pointer border border-border bg-background" data-testid="affiliate-accent-picker" />
            <Input data-testid="affiliate-accent" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="w-32 rounded-none mono" placeholder="#7c3aed" />
            {form.accent_color && <button className="mono text-[10px] uppercase text-muted-foreground hover:text-foreground" onClick={() => setForm({ ...form, accent_color: "" })}>clear</button>}
          </div>
          <div className="mt-5 border-t border-border pt-4" data-testid="affiliate-support-settings">
            <div className="font-heading text-base font-bold">Customer support contact</div>
            <p className="mt-1 text-[11px] text-muted-foreground">Shown in your shop's footer, on order and tracking pages and in your customers' order emails, so they contact <b>you</b> about their orders. Fill in any or all.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <div><label className="mono block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Telegram username</label>
                <Input data-testid="affiliate-support-telegram" value={form.support_telegram} onChange={(e) => setForm({ ...form, support_telegram: e.target.value })} className="mt-1 rounded-none mono text-xs" placeholder="@yourname" /></div>
              <div><label className="mono block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Support email</label>
                <Input data-testid="affiliate-support-email" value={form.support_email} onChange={(e) => setForm({ ...form, support_email: e.target.value })} className="mt-1 rounded-none mono text-xs" placeholder="help@yourshop.com" /></div>
              <div><label className="mono block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Contact link (https://…)</label>
                <Input data-testid="affiliate-support-url" value={form.support_url} onChange={(e) => setForm({ ...form, support_url: e.target.value })} className="mt-1 rounded-none mono text-xs" placeholder="https://…" /></div>
            </div>
          </div>
          <button data-testid="affiliate-save-brand" onClick={save} disabled={saving}
            className="mt-4 inline-flex items-center gap-1.5 bg-primary px-4 py-2 text-xs font-medium text-primary-foreground">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save branding
          </button>
          </>)}

          <div className={`${white ? "mt-6 border-t border-border pt-4" : ""}`} data-testid="affiliate-payout-settings">
            <div className="font-heading text-base font-bold">Get paid automatically</div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {settings.auto_payout_enabled
                ? <>Automatic payouts are <b className="text-primary">on</b>: when a referred order is paid, your cut is sent to the address below in the same coin the customer used{settings.payout_fee_gbp > 0 ? <>, less a £{settings.payout_fee_gbp.toFixed(2)} network-fee allowance</> : null}. Double-check these addresses — crypto sent to a wrong address cannot be recovered.</>
                : <>Save your addresses here; once automatic payouts are switched on for your account, each paid order sends your cut straight to them.</>}
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {["BTC", "LTC"].map((cur) => (
                <div key={cur}>
                  <label className="mono block text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{cur === "BTC" ? "Bitcoin" : "Litecoin"} address</label>
                  <Input data-testid={`affiliate-payout-${cur}`} value={form.payout_addresses[cur]} spellCheck={false}
                    onChange={(e) => setForm({ ...form, payout_addresses: { ...form.payout_addresses, [cur]: e.target.value.trim() } })}
                    className="mt-1 rounded-none mono text-xs" placeholder={cur === "BTC" ? "bc1q…" : "ltc1q…"} />
                </div>
              ))}
            </div>
            <button data-testid="affiliate-save-payout" onClick={save} disabled={saving}
              className="mt-3 inline-flex items-center gap-1.5 border border-primary px-4 py-2 text-xs font-medium text-primary">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save payout addresses
            </button>
          </div>
        </div>

        <div className="border border-border bg-card p-5" data-testid="affiliate-recent">
          <div className="font-heading text-base font-bold">Your referred orders</div>
          {stats.recent.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground" data-testid="affiliate-recent-empty">No referred orders yet — share your link to get started.</p>
          ) : (
            <div className="mt-3 max-h-[32rem] space-y-3 overflow-y-auto pr-1">
              {stats.recent.map((o) => (
                <div key={o.order_number} className="border border-border bg-background p-3 text-sm" data-testid={`affiliate-order-${o.order_number}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="mono font-bold">{o.order_number}</span>
                      <span className="ml-3 text-xs text-muted-foreground" data-testid={`affiliate-order-when-${o.order_number}`}>{fmtWhen(o.created_at)}</span>
                    </div>
                    <div className="mono flex flex-wrap gap-1.5 text-[10px] uppercase tracking-[0.12em]">
                      <span className={`border px-1.5 py-0.5 ${o.payment_status === "paid" ? "border-green-600 text-green-700" : "border-border text-muted-foreground"}`}>{STATUS[o.payment_status] || o.payment_status}</span>
                      <span className="border border-border px-1.5 py-0.5 text-muted-foreground">{FULFIL[o.fulfillment_status] || o.fulfillment_status}</span>
                    </div>
                  </div>
                  <ul className="mt-2 space-y-1 text-xs" data-testid={`affiliate-order-items-${o.order_number}`}>
                    {(o.items || []).map((it, i) => (
                      <li key={i}>
                        <span className="font-medium">{it.label}</span>
                        {it.detail?.length > 0 && <span className="text-muted-foreground"> — {it.detail.join("; ")}</span>}
                      </li>
                    ))}
                  </ul>
                  <div className="mono mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                    {o.shipping_zone && <span>Ships to: {o.shipping_zone}</span>}
                    {o.discount_code && <span>Code: {o.discount_code}</span>}
                    {o.tracking_number && <span>Tracking: {o.tracking_number}</span>}
                    <span className="ml-auto">Sale {gbp(o.sale || 0)} · Shipping {gbp(o.shipping || 0)} · <b className="text-foreground">Your cut {gbp(o.commission || 0)}</b></span>
                  </div>
                  {o.payout && (
                    <div className="mono mt-1 text-[11px]" data-testid={`affiliate-order-payout-${o.order_number}`}>
                      {o.payout.status === "created"
                        ? <span className="text-green-700">Paid out: {Number(o.payout.crypto_amount).toFixed(8)} {o.payout.currency} ({gbp(o.payout.amount_gbp || 0)}){o.payout.state ? ` · ${o.payout.state}` : ""}</span>
                        : o.payout.status === "failed"
                          ? <span className="text-destructive">Payout failed — we've been alerted and will resend</span>
                          : <span className="text-muted-foreground">Payout pending</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-[11px] text-muted-foreground">Customer names and delivery details stay private; you see what was ordered and where it stands. Commission counts once the customer's payment is confirmed.</p>
        </div>
      </div>
    </section>
  );
};
